/**
 * PWASplashLinks
 *
 * Injeta dinamicamente os <link rel="apple-touch-startup-image"> que o
 * iOS Safari usa para exibir a splash screen quando o PWA é aberto da
 * Tela de Início. Cada size precisa ter sua tag com `media` query
 * específica — caso contrário o iOS cai pra tela branca.
 *
 * Endpoint backend: /api/pwa/splash.png?w=W&h=H — gera PNG on-demand
 * com a logo do município centralizada sobre a cor primária.
 *
 * Render component is a noop (apenas side-effect).
 */
import { useEffect } from 'react';

// Pares (width, height, ratio) que cobrem os iPhones modernos + iPads.
// Lista oficial Apple Developer Docs ("apple-touch-startup-image").
const APPLE_SPLASH_SIZES = [
  // iPhone 14 Pro Max, 15 Pro Max
  { w: 1290, h: 2796, ratio: 3, mw: 430, mh: 932 },
  // iPhone 14 Pro, 15 Pro
  { w: 1179, h: 2556, ratio: 3, mw: 393, mh: 852 },
  // iPhone 14 Plus, 13 Pro Max, 12 Pro Max
  { w: 1284, h: 2778, ratio: 3, mw: 428, mh: 926 },
  // iPhone 14, 13 Pro, 13, 12 Pro, 12
  { w: 1170, h: 2532, ratio: 3, mw: 390, mh: 844 },
  // iPhone 13 mini, 12 mini, 11 Pro, XS, X
  { w: 1125, h: 2436, ratio: 3, mw: 375, mh: 812 },
  // iPhone 11, XR
  { w: 828, h: 1792, ratio: 2, mw: 414, mh: 896 },
  // iPhone 11 Pro Max, XS Max
  { w: 1242, h: 2688, ratio: 3, mw: 414, mh: 896 },
  // iPhone 8 Plus, 7 Plus, 6S Plus, 6 Plus
  { w: 1242, h: 2208, ratio: 3, mw: 414, mh: 736 },
  // iPhone 8, 7, 6S, 6, SE 2nd gen
  { w: 750, h: 1334, ratio: 2, mw: 375, mh: 667 },
  // iPhone SE 1st gen, 5s, 5C, 5
  { w: 640, h: 1136, ratio: 2, mw: 320, mh: 568 },
  // iPad Pro 12.9"
  { w: 2048, h: 2732, ratio: 2, mw: 1024, mh: 1366 },
  // iPad Pro 11"
  { w: 1668, h: 2388, ratio: 2, mw: 834, mh: 1194 },
  // iPad Air 10.5"
  { w: 1668, h: 2224, ratio: 2, mw: 834, mh: 1112 },
  // iPad 10.2"
  { w: 1620, h: 2160, ratio: 2, mw: 810, mh: 1080 },
  // iPad Mini, Air
  { w: 1536, h: 2048, ratio: 2, mw: 768, mh: 1024 },
];

export default function PWASplashLinks() {
  useEffect(() => {
    // Remove versões antigas (hot reload + re-renders)
    document
      .querySelectorAll('link[rel="apple-touch-startup-image"][data-dynamic="1"]')
      .forEach((node) => node.parentNode && node.parentNode.removeChild(node));

    APPLE_SPLASH_SIZES.forEach(({ w, h, ratio, mw, mh }) => {
      // Portrait
      const linkP = document.createElement('link');
      linkP.rel = 'apple-touch-startup-image';
      linkP.setAttribute('data-dynamic', '1');
      linkP.media = `(device-width: ${mw}px) and (device-height: ${mh}px) and (-webkit-device-pixel-ratio: ${ratio}) and (orientation: portrait)`;
      linkP.href = `/api/pwa/splash.png?w=${w}&h=${h}`;
      document.head.appendChild(linkP);

      // Landscape (swap w/h)
      const linkL = document.createElement('link');
      linkL.rel = 'apple-touch-startup-image';
      linkL.setAttribute('data-dynamic', '1');
      linkL.media = `(device-width: ${mw}px) and (device-height: ${mh}px) and (-webkit-device-pixel-ratio: ${ratio}) and (orientation: landscape)`;
      linkL.href = `/api/pwa/splash.png?w=${h}&h=${w}`;
      document.head.appendChild(linkL);
    });
  }, []);

  return null;
}
